// @ts-nocheck
/**
 * RBAC Cache Management Component
 * Carbon Design System interface for RBAC cache monitoring and management
 * Displays cache hit/miss rates, manages cache invalidation, and pre-warms caches
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  InlineNotification,
  TextInput,
  Modal,
  Loading,
  Heading,
  Toggle,
  NumberInput,
} from '@carbon/react';
import {
  Renew,
  TrashCan,
  Analytics,
  Dashboard as Heatmap,
  CheckmarkFilled,
  WarningFilled,
  ErrorFilled,
  DataBase as Database,
  CloudDataOps,
  Time,
} from '@carbon/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styles from './RBACCacheManagement.module.scss';

interface CacheStats {
  kv: {
    total_keys: number;
    cached_users: number;
    hit_rate: number;
    miss_rate: number;
    avg_response_time_ms: number;
    available: boolean;
  };
  durable_objects: {
    active_instances: number;
    total_requests: number;
    hit_rate: number;
    avg_response_time_ms: number;
    available: boolean;
  };
  request_cache: {
    hits: number;
    misses: number;
    hit_rate: number;
    total_requests: number;
  };
  overall: {
    total_users: number;
    cache_coverage_percent: number;
    overall_hit_rate: number;
    cache_ttl_hours: number;
    last_updated: string;
  };
  historical?: Array<{
    timestamp: string;
    kv_hit_rate: number;
    durable_objects_hit_rate: number;
    request_cache_hit_rate: number;
    total_requests: number;
    avg_response_time_ms: number;
  }>;
}

interface RBACCacheManagementProps {
  apiBaseUrl?: string;
}

const RBACCacheManagement: React.FC<RBACCacheManagementProps> = ({ apiBaseUrl = '/api/v1' }) => {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invalidateModalOpen, setInvalidateModalOpen] = useState(false);
  const [warmModalOpen, setWarmModalOpen] = useState(false);
  const [userIdsInput, setUserIdsInput] = useState('');
  const [roleIdsInput, setRoleIdsInput] = useState('');
  const [invalidateAll, setInvalidateAll] = useState(false);
  const [warmAll, setWarmAll] = useState(false);
  const [warmRecent, setWarmRecent] = useState(false);
  const [sinceMinutes, setSinceMinutes] = useState(5);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadCacheStats();
    const interval = setInterval(loadCacheStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getAuthToken = () => {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || '';
  };

  const loadCacheStats = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/admin/rbac/cache/stats`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load cache stats: ${response.status}`);
      }

      const data = await response.json();
      setStats(data.statistics);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cache statistics');
      console.error('Error loading cache stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidateCache = async () => {
    setOperationInProgress(true);
    setError(null);

    try {
      const userIds = userIdsInput
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);
      const roleIds = roleIdsInput
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      const body: any = {};
      if (invalidateAll) {
        body.invalidate_all = true;
      } else if (userIds.length > 0) {
        body.user_ids = userIds;
      } else if (roleIds.length > 0) {
        body.role_ids = roleIds;
      } else {
        throw new Error('Please specify user IDs, role IDs, or select invalidate all');
      }

      const response = await fetch(`${apiBaseUrl}/admin/rbac/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Cache invalidation failed: ${response.status}`);
      }

      const result = await response.json();
      setSuccessMessage(
        `Successfully invalidated ${result.invalidated_count} cache entries in ${result.duration_ms}ms`
      );
      setInvalidateModalOpen(false);
      setUserIdsInput('');
      setRoleIdsInput('');
      setInvalidateAll(false);
      loadCacheStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cache invalidation failed');
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleWarmCache = async () => {
    setOperationInProgress(true);
    setError(null);

    try {
      const userIds = userIdsInput
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      const body: any = {};
      if (warmAll) {
        body.warm_all = true;
      } else if (warmRecent) {
        body.warm_recent = true;
        body.since_minutes = sinceMinutes;
      } else if (userIds.length > 0) {
        body.user_ids = userIds;
      } else {
        throw new Error('Please specify user IDs or select a warming option');
      }

      const response = await fetch(`${apiBaseUrl}/admin/rbac/cache/warm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Cache warming failed: ${response.status}`);
      }

      const result = await response.json();
      setSuccessMessage(`Successfully warmed ${result.users_warmed} user caches in ${result.duration_ms}ms`);
      setWarmModalOpen(false);
      setUserIdsInput('');
      setWarmAll(false);
      setWarmRecent(false);
      loadCacheStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cache warming failed');
    } finally {
      setOperationInProgress(false);
    }
  };

  const renderMetricTile = (
    title: string,
    value: string | number,
    subtitle?: string,
    status?: 'good' | 'warning' | 'error',
    icon?: React.ComponentType
  ) => (
    <Tile className={styles.metricTile}>
      <div className={styles.metricHeader}>
        {icon && React.createElement(icon, { size: 24, className: styles.metricIcon })}
        <Heading as="h4">{title}</Heading>
      </div>
      <div className={styles.metricValue}>
        {status === 'error' && <ErrorFilled size={20} className={styles.errorIcon} />}
        {status === 'warning' && <WarningFilled size={20} className={styles.warningIcon} />}
        {status === 'good' && <CheckmarkFilled size={20} className={styles.successIcon} />}
        <span className={styles.value}>{value}</span>
      </div>
      {subtitle && <p className={styles.metricSubtitle}>{subtitle}</p>}
    </Tile>
  );

  const prepareChartData = () => {
    if (!stats?.historical || stats.historical.length === 0) return [];

    return stats.historical.map(point => ({
      time: new Date(point.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      'KV Cache': point.kv_hit_rate.toFixed(1),
      'Request Cache': point.request_cache_hit_rate.toFixed(1),
      'Avg Response (ms)': point.avg_response_time_ms.toFixed(1),
    }));
  };

  if (loading) {
    return <Loading description="Loading cache statistics..." withOverlay={false} />;
  }

  return (
    <div className={styles.rbacCacheManagement}>
      <div className={styles.header}>
        <div>
          <Heading as="h2">RBAC Cache Management</Heading>
          <p className={styles.description}>Monitor and manage RBAC permission cache performance</p>
        </div>

        <div className={styles.actions}>
          <Button kind="secondary" renderIcon={Renew} onClick={loadCacheStats} disabled={loading}>
            Refresh
          </Button>
          <Button kind="tertiary" renderIcon={Heatmap} onClick={() => setWarmModalOpen(true)}>
            Warm Cache
          </Button>
          <Button kind="danger--tertiary" renderIcon={TrashCan} onClick={() => setInvalidateModalOpen(true)}>
            Invalidate Cache
          </Button>
        </div>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      {successMessage && (
        <InlineNotification
          kind="success"
          title="Success"
          subtitle={successMessage}
          onCloseButtonClick={() => setSuccessMessage(null)}
          lowContrast
          timeout={5000}
        />
      )}

      {/* Cache Metrics Overview */}
      <section className={styles.metricsSection}>
        <Heading as="h3">Cache Performance Metrics</Heading>
        <Grid className={styles.metricsGrid}>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile(
              'Overall Hit Rate',
              `${stats?.overall.overall_hit_rate.toFixed(1)}%`,
              'Combined cache efficiency',
              stats && stats.overall.overall_hit_rate >= 85
                ? 'good'
                : stats && stats.overall.overall_hit_rate >= 70
                  ? 'warning'
                  : 'error',
              Analytics
            )}
          </Column>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile(
              'Cache Coverage',
              `${stats?.overall.cache_coverage_percent.toFixed(1)}%`,
              `${stats?.kv.cached_users} of ${stats?.overall.total_users} users cached`,
              stats && stats.overall.cache_coverage_percent >= 90
                ? 'good'
                : stats && stats.overall.cache_coverage_percent >= 75
                  ? 'warning'
                  : 'error',
              Database
            )}
          </Column>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile(
              'KV Response Time',
              `${stats?.kv.avg_response_time_ms.toFixed(1)}ms`,
              'Average response time',
              stats && stats.kv.avg_response_time_ms <= 10
                ? 'good'
                : stats && stats.kv.avg_response_time_ms <= 50
                  ? 'warning'
                  : 'error',
              Time
            )}
          </Column>
        </Grid>

        <Grid className={styles.metricsGrid} style={{ marginTop: '1rem' }}>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile('KV Cache Hit Rate', `${stats?.kv.hit_rate.toFixed(1)}%`, 'Edge cache performance')}
          </Column>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile(
              'Request Cache Hit Rate',
              `${stats?.request_cache.hit_rate.toFixed(1)}%`,
              'In-memory cache hits'
            )}
          </Column>
          <Column lg={4} md={4} sm={4}>
            {renderMetricTile('Cache TTL', `${stats?.overall.cache_ttl_hours}h`, 'Time to live')}
          </Column>
        </Grid>
      </section>

      {/* Historical Performance Chart */}
      {stats?.historical && stats.historical.length > 0 && (
        <section className={styles.chartSection}>
          <Heading as="h3">Cache Performance (Last 24 Hours)</Heading>
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={prepareChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="KV Cache" stroke="#0f62fe" strokeWidth={2} />
                <Line type="monotone" dataKey="Request Cache" stroke="#24a148" strokeWidth={2} />
                <Line type="monotone" dataKey="Avg Response (ms)" stroke="#ff832b" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Cache Statistics Table */}
      <section className={styles.tableSection}>
        <Heading as="h3">Cache Layer Statistics</Heading>
        <DataTable
          rows={[
            {
              id: '1',
              layer: 'KV Cache',
              status: stats?.kv.available ? 'Available' : 'Unavailable',
              hits: stats?.kv.total_keys || 0,
              hitRate: `${stats?.kv.hit_rate.toFixed(1)}%`,
              avgResponse: `${stats?.kv.avg_response_time_ms.toFixed(1)}ms`,
            },
            {
              id: '2',
              layer: 'Request Cache',
              status: 'Available',
              hits: stats?.request_cache.hits || 0,
              hitRate: `${stats?.request_cache.hit_rate.toFixed(1)}%`,
              avgResponse: '< 1ms',
            },
            {
              id: '3',
              layer: 'Durable Objects',
              status: stats?.durable_objects.available ? 'Available' : 'Not Used',
              hits: stats?.durable_objects.total_requests || 0,
              hitRate: `${stats?.durable_objects.hit_rate.toFixed(1)}%`,
              avgResponse: `${stats?.durable_objects.avg_response_time_ms.toFixed(1)}ms`,
            },
          ]}
          headers={[
            { key: 'layer', header: 'Cache Layer' },
            { key: 'status', header: 'Status' },
            { key: 'hits', header: 'Total Hits' },
            { key: 'hitRate', header: 'Hit Rate' },
            { key: 'avgResponse', header: 'Avg Response' },
          ]}
        >
          {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </section>

      {/* Cache Invalidation Modal */}
      <Modal
        open={invalidateModalOpen}
        onRequestClose={() => setInvalidateModalOpen(false)}
        modalHeading="Invalidate RBAC Cache"
        modalLabel="Cache Management"
        primaryButtonText="Invalidate"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleInvalidateCache}
        primaryButtonDisabled={operationInProgress}
      >
        <div className={styles.modalContent}>
          <Toggle
            id="invalidate-all-toggle"
            labelText="Invalidate all user caches"
            toggled={invalidateAll}
            onToggle={setInvalidateAll}
          />

          {!invalidateAll && (
            <>
              <TextInput
                id="user-ids"
                labelText="User IDs"
                placeholder="USER-001, USER-002, USER-003"
                helperText="Enter user IDs separated by commas"
                value={userIdsInput}
                onChange={e => setUserIdsInput(e.target.value)}
                disabled={invalidateAll}
              />

              <TextInput
                id="role-ids"
                labelText="Role IDs"
                placeholder="ROLE-ADMIN, ROLE-TEACHER"
                helperText="Or enter role IDs to invalidate all users with those roles"
                value={roleIdsInput}
                onChange={e => setRoleIdsInput(e.target.value)}
                disabled={invalidateAll}
              />
            </>
          )}

          <InlineNotification
            kind="warning"
            title="Performance Impact"
            subtitle="Cache invalidation will temporarily increase database load until caches are rebuilt."
            lowContrast
          />
        </div>
      </Modal>

      {/* Cache Warming Modal */}
      <Modal
        open={warmModalOpen}
        onRequestClose={() => setWarmModalOpen(false)}
        modalHeading="Warm RBAC Cache"
        modalLabel="Cache Management"
        primaryButtonText="Warm Cache"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleWarmCache}
        primaryButtonDisabled={operationInProgress}
      >
        <div className={styles.modalContent}>
          <Toggle id="warm-all-toggle" labelText="Warm all user caches" toggled={warmAll} onToggle={setWarmAll} />

          <Toggle
            id="warm-recent-toggle"
            labelText="Warm recently updated users only"
            toggled={warmRecent}
            onToggle={setWarmRecent}
            disabled={warmAll}
          />

          {warmRecent && !warmAll && (
            <NumberInput
              id="since-minutes"
              label="Updated within (minutes)"
              value={sinceMinutes}
              onChange={(e, state) => setSinceMinutes(state.value)}
              min={1}
              max={1440}
              step={1}
            />
          )}

          {!warmAll && !warmRecent && (
            <TextInput
              id="user-ids-warm"
              labelText="User IDs"
              placeholder="USER-001, USER-002, USER-003"
              helperText="Enter user IDs separated by commas"
              value={userIdsInput}
              onChange={e => setUserIdsInput(e.target.value)}
              disabled={warmAll || warmRecent}
            />
          )}

          <InlineNotification
            kind="info"
            title="Cache Warming"
            subtitle="Pre-warming caches improves performance by computing permissions in advance."
            lowContrast
          />
        </div>
      </Modal>
    </div>
  );
};

export default RBACCacheManagement;
